import type { PerformancePart } from '@tunasong/schemas'
import { normalizeParts, performancePartLabels, performanceParts } from '@tunasong/schemas'
import { Button, Checkbox, FormControlLabel, ToggleButton, TunaDialog } from '@tunasong/ui-lib'
import { useId, useState, type FC } from 'react'
import type { BandRef } from './song-band.hook.js'
import styles from './part-prompts.module.css'

export interface PartPickerSheetProps {
  open: boolean
  /** The song's band; `null` for personal songs, where the choice becomes the default parts. */
  band: BandRef | null
  hasDefault: boolean
  onChoose: (parts: PerformancePart[], options: { alsoDefault: boolean }) => void
  /** Closed without choosing: the song shows the Full chart for this visit. */
  onDismiss: () => void
}

/** Toggle a part while picking. Full chart is exclusive; unlike the header menu, the selection may be empty. */
const toggleSelection = (current: readonly PerformancePart[], part: PerformancePart): PerformancePart[] => {
  if (part === 'full') {
    return current.includes('full') ? [] : ['full']
  }
  const others = current.filter(p => p !== 'full')
  return others.includes(part) ? others.filter(p => p !== part) : [...others, part]
}

/**
 * First-entry sheet: "What do you play in {Band}?". Several parts can be picked. Done stays disabled
 * until at least one is, so an empty set is never saved.
 */
export const PartPickerSheet: FC<PartPickerSheetProps> = props => {
  const { open, band, hasDefault, onChoose, onDismiss } = props
  const [selected, setSelected] = useState<PerformancePart[]>([])
  const [alsoDefault, setAlsoDefault] = useState(!hasDefault)
  const titleId = useId()

  const title = band ? `What do you play in ${band.name}?` : 'What are you playing?'
  const chosen = normalizeParts(selected)

  const handleDone = () => {
    if (!chosen) {
      return
    }
    onChoose(chosen, { alsoDefault: band ? alsoDefault : false })
  }

  return (
    <TunaDialog open={open} onClose={onDismiss} aria-labelledby={titleId} maxWidth="sm" className={styles.sheet}>
      <h2 id={titleId} className={styles.title}>
        {title}
      </h2>
      <p className={styles.hint}>Pick all that apply</p>
      <div className={styles.parts}>
        {performanceParts.map(part => (
          <ToggleButton
            key={part}
            value={part}
            className={styles.part}
            selected={selected.includes(part)}
            onClick={() => setSelected(current => toggleSelection(current, part))}
          >
            {performancePartLabels[part]}
          </ToggleButton>
        ))}
      </div>
      {band ? (
        <FormControlLabel
          control={<Checkbox checked={alsoDefault} onCheckedChange={setAlsoDefault} />}
          label="Also make these my default parts"
        />
      ) : null}
      <div className={styles.actions}>
        <Button variant="text" onClick={onDismiss}>
          Not now
        </Button>
        <Button variant="contained" disabled={!chosen} onClick={handleDone}>
          Done
        </Button>
      </div>
    </TunaDialog>
  )
}

export default PartPickerSheet
